import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  InputGroup,
  Alert,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import {
  Bell,
  BoxArrowRight,
  PersonCircle,
  Search,
  ArrowLeft,
  CheckCircleFill,
  CalendarEvent,
  Collection,
  Tag,
  Clock,
  XCircleFill,
  PencilSquare,
  ChevronRight,
  TrashFill,
} from "react-bootstrap-icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_CREATE_LEARN = `${API_BASE}/createLearn`;
const ENDPOINT_UPDATE_LEARN = `${API_BASE}/updateLearn`;
const ENDPOINT_UPLOAD = `${API_BASE}/uploadDocument`;
const ENDPOINT_HISTORY = `${API_BASE}/getActivitiesByUser`;
const ENDPOINT_SELECTS = `${API_BASE}/getLearnSelect`;
const ENDPOINT_ACTIVITY_BY_ID = `${API_BASE}/getActivityById`;
const ENDPOINT_LOGGEDIN = `${API_BASE}/getLoggedInDetails`;
const ENDPOINT_DELETE_ACTIVITY = `${API_BASE}/deleteActivity`;
const ENDPOINT_AUTOFILL_AI_CHECKING = `${API_BASE}/chkdocuai`;

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";
const BRAND = "#3D74F5";

const STATUS_STYLES = {
  Pending: {
    bg: "rgba(245,158,11,.14)",
    text: "#B45309",
    icon: <Clock size={14} />,
    accent: "linear-gradient(180deg, #F59E0B, #D97706)",
  },
  Approved: {
    bg: "rgba(16,185,129,.14)",
    text: "#047857",
    icon: <CheckCircleFill size={14} />,
    accent: "linear-gradient(180deg, #10B981, #047857)",
  },
  Rejected: {
    bg: "rgba(239,68,68,.14)",
    text: "#B91C1C",
    icon: <XCircleFill size={14} />,
    accent: "linear-gradient(180deg, #EF4444, #B91C1C)",
  },
  "Re-submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={14} />,
    accent: "linear-gradient(180deg, #3B82F6, #1D4ED8)",
  },
  "Re-Submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={14} />,
    accent: "linear-gradient(180deg, #3B82F6, #1D4ED8)",
  },
  Draft: {
    bg: "rgba(139,92,246,.14)",
    text: "#6D28D9",
    icon: <PencilSquare size={14} />,
    accent: "linear-gradient(180deg, #8B5CF6, #6D28D9)",
  },
  Default: {
    bg: "rgba(148,163,184,.16)",
    text: "#475569",
    icon: <Clock size={14} />,
    accent: "linear-gradient(180deg, #94A3B8, #64748B)",
  },
};

function statusPillStyle(s) {
  const { bg, text } = STATUS_STYLES[s] || STATUS_STYLES.Default;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    background: bg,
    color: text,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
}

const cardWrap = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "16px 18px",
  boxShadow: "0 2px 10px rgba(2,6,23,.05)",
  transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
};

function TagChip({ icon, label }) {
  return (
    <span
      className="d-flex align-items-center"
      style={{
        gap: 6,
        fontSize: 12.5,
        fontWeight: 600,
        color: "#334155",
        background: "#F1F5F9",
        border: "1px solid #E2E8F0",
        borderRadius: 999,
        padding: "4px 10px",
      }}
    >
      {icon}
      {label || "—"}
    </span>
  );
}

function ActivityCard({
  it,
  fromText,
  toText,
  typeText,
  catText,
  onEdit,
  onViewProfile,
  onDelete,
}) {
  const status = it?.status_name || "Pending";
  const pill = STATUS_STYLES[status] || STATUS_STYLES.Default;
  const isDraft = String(status).toLowerCase() === "draft";
  const canEdit =
    String(status).toLowerCase() === "re-submit" ||
    String(status).toLowerCase() === "resubmit" ||
    String(status).toLowerCase() === "pending" ||
    isDraft;

  const dateDisplay = `${fromText || "—"} – ${toText || "—"}`;

  const handleClick = () => {
    if (canEdit) onEdit?.(it);
    else onViewProfile?.();
  };

  const setFocusRing = (el, on) => {
    el.style.boxShadow = on
      ? "0 0 0 3px rgba(61,116,245,.35), 0 10px 26px rgba(2,6,23,.10)"
      : cardWrap.boxShadow;
  };

  return (
    <div
      style={{ ...cardWrap, marginBottom: 12, cursor: "pointer", outline: "none" }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      onFocus={(e) => setFocusRing(e.currentTarget, true)}
      onBlur={(e) => setFocusRing(e.currentTarget, false)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 10px 26px rgba(2,6,23,.10)";
        e.currentTarget.style.borderColor = "#cbd5e1";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = cardWrap.boxShadow;
        e.currentTarget.style.borderColor = "#e5e7eb";
      }}
    >
        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
          <div
            className="d-flex align-items-center"
            style={{ gap: 7, color: "#1F2937", fontSize: 13 }}
          >
            <CalendarEvent size={15} />
            <span style={{ fontWeight: 600 }}>{dateDisplay}</span>
          </div>

          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <span style={statusPillStyle(status)}>
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                {pill.icon}
                {status}
              </span>
            </span>

            {isDraft && (
              <button
                type="button"
                title="Delete draft"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(it);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "1px solid #FECACA",
                  background: "#FEF2F2",
                  color: "#B91C1C",
                  cursor: "pointer",
                }}
              >
                <TrashFill size={13} />
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            fontWeight: 700,
            color: "#0f172a",
            fontSize: 16,
            lineHeight: 1.35,
            margin: "10px 0 12px",
          }}
        >
          {it.activity_name || it.name || "-"}
        </div>

        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <TagChip icon={<Collection size={13} />} label={typeText} />
          <TagChip icon={<Tag size={13} />} label={catText} />
        </div>

        <div
          className="d-flex align-items-center justify-content-between mt-3 pt-2"
          style={{ borderTop: "1px dashed #e2e8f0" }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: canEdit ? "#2563EB" : "#64748b",
            }}
          >
            {canEdit
              ? "Click to update your submission"
              : "Click to view your LEARN submission(s)"}
          </span>
          <ChevronRight size={13} color={canEdit ? "#2563EB" : "#64748b"} />
        </div>
    </div>
  );
}

function Required() {
  return (
    <span className="ms-1" style={{ color: "#DC2626" }} aria-hidden="true">
      *
    </span>
  );
}

function StepHeader({ number, title, description }) {
  return (
    <div className="d-flex align-items-start mb-3" style={{ gap: 12 }}>
      <div
        style={{
          width: 28,
          height: 28,
          minWidth: 28,
          borderRadius: "50%",
          background: "#2563EB",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        {number}
      </div>
      <div>
        <div className="fw-bold" style={{ fontSize: 15, color: "#0f172a" }}>
          {title}
        </div>
        <div
          className="text-muted"
          style={{ fontSize: 13, lineHeight: 1.6, marginTop: 2 }}
        >
          {description}
        </div>
      </div>
    </div>
  );
}

function UploadCheckingModal({
  show,
  onHide,
  stage,
  pct,
  title,
  message,
  details,
}) {
  const isDone = stage === "done";
  const isChecking = stage === "checking";

  return (
    <Modal
      show={show}
      onHide={isDone ? onHide : undefined}
      centered
      backdrop="static"
      keyboard={isDone}
      className="custom-font"
    >
      <Modal.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="fw-bold" style={{ fontSize: 16 }}>
            {title}
          </div>
          {isDone && (
            <Button size="sm" variant="outline-secondary" onClick={onHide}>
              Close
            </Button>
          )}
        </div>

        <div
          className="text-muted mb-3"
          style={{ fontSize: 13, lineHeight: 1.5 }}
        >
          {message}
        </div>

        <ProgressBar now={pct} label={`${pct}%`} className="mb-2" />

        <div
          className="d-flex align-items-center justify-content-between mt-2"
          style={{ fontSize: 12 }}
        >
          <div className="text-muted">
            {isChecking
              ? "checking your documents…"
              : stage === "uploading"
                ? "Uploading…"
                : "Completed"}
          </div>
          <div className="text-muted">{details}</div>
        </div>

        {isChecking && (
          <div
            className="mt-3 d-flex align-items-center gap-2 text-muted"
            style={{ fontSize: 12 }}
          >
            <Spinner animation="border" size="sm" /> Please wait, this may take
            a moment…
          </div>
        )}

        {isDone && (
          <div
            className="mt-3 d-flex align-items-center gap-2"
            style={{ color: "#047857", fontWeight: 700 }}
          >
            <CheckCircleFill /> Completed
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function LearnSubmission() {
  const [form, setForm] = useState({
    dateFrom: "",
    dateTo: "",
    activityName: "",
    activityTypeId: "",
    activityCategoryId: "",
    activityOther: "",
    activityProvider: "",
    isParticipated: false,
    trainerActaAclp: "",
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [proofOtherFiles, setProofOtherFiles] = useState([]);
  const [proofPpFiles, setProofPpFiles] = useState([]);
  const proofOtherRef = useRef(null);
  const proofPpRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiExtractDone, setAiExtractDone] = useState(false);
  const [aiExtractMsg, setAiExtractMsg] = useState("");
  const [aiExtractEvidence, setAiExtractEvidence] = useState([]);

  const [selects, setSelects] = useState({
    activityTypes: [],
    learnCategories: [],
  });
  const [loadingSelects, setLoadingSelects] = useState(true);
  const [selectsError, setSelectsError] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hisError, setHisError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [q, setQ] = useState("");
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [year, setYear] = useState(String(currentYear));
  const limit = 8;

  const [showSuccess, setShowSuccess] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editingStatus, setEditingStatus] = useState("");
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [wasDraftSave, setWasDraftSave] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [editRemarks, setEditRemarks] = useState("");
  const [existingDocs, setExistingDocs] = useState({
    cc: [],
    vl: [],
    sst: [],
    pp: [],
  });
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Modal states for upload + checking
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalStage, setModalStage] = useState("uploading"); // uploading | checking | done
  const [modalPct, setModalPct] = useState(0);
  const [modalTitle, setModalTitle] = useState("Uploading Documents");
  const [modalMsg, setModalMsg] = useState(
    "Uploading your supporting documents. Please do not close this page.",
  );
  const [modalDetails, setModalDetails] = useState("");

  const FRONTEND_MIN_START = "2026-03-01";
  const [cycle, setCycle] = useState({
    start: FRONTEND_MIN_START,
    end: "",
    daysRemaining: null,
    eligible: true,
    learnReq: null,
    doReq: null,
  });
  const [loadingCycle, setLoadingCycle] = useState(true);

  const navigate = useNavigate();

  const token = useMemo(
    () =>
      localStorage.getItem("authToken") || localStorage.getItem("token") || "",
    [],
  );

  const { displayName } = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return {
        displayName:
          localStorage.getItem("display_name") ||
          user.display_name ||
          localStorage.getItem("username") ||
          "User Acc",
      };
    } catch {
      return {
        displayName:
          localStorage.getItem("display_name") ||
          localStorage.getItem("username") ||
          "User Acc",
      };
    }
  }, []);

  const userId = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        const id =
          u?.user_id ??
          u?.id ??
          u?.userId ??
          u?.data?.user_id ??
          u?.data?.id ??
          u?.data?.userId ??
          "";
        if (id) return String(id);
      }
      const loose =
        localStorage.getItem("user_id") ||
        localStorage.getItem("userid") ||
        localStorage.getItem("uid") ||
        "";
      return loose ? String(loose) : "";
    } catch {
      return "";
    }
  }, []);

  const todayYMD = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const maxDate = todayYMD();

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setModalStage("uploading");
    setModalPct(0);
    setModalTitle("");
    setModalMsg("");
    setModalDetails("");
  };

  // Load cycle dates
  useEffect(() => {
    (async () => {
      try {
        setLoadingCycle(true);
        const { data } = await axios.get(ENDPOINT_LOGGEDIN, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const obj = data?.data || {};
        let cycleStart = obj?.cycleStart || FRONTEND_MIN_START;
        let cycleEnd = obj?.cycleEnd || "";
        if (cycleStart < FRONTEND_MIN_START) cycleStart = FRONTEND_MIN_START;

        setCycle({
          start: cycleStart,
          end: cycleEnd,
          daysRemaining: obj?.daysRemaining ?? null,
          eligible: obj?.eligible ?? true,
          learnReq: obj?.noOfLearnReq ?? obj?.learnReq ?? null,
          doReq: obj?.noOfDoReq ?? obj?.doReq ?? null,
        });

        setForm((f) => {
          const next = { ...f };
          if (next.dateFrom && cycleStart && next.dateFrom < cycleStart)
            next.dateFrom = "";
          if (next.dateTo && cycleStart && next.dateTo < cycleStart)
            next.dateTo = "";
          if (next.dateFrom && cycleEnd && next.dateFrom > cycleEnd)
            next.dateFrom = "";
          if (next.dateTo && cycleEnd && next.dateTo > cycleEnd)
            next.dateTo = "";
          if (next.dateFrom && next.dateTo && next.dateTo < next.dateFrom)
            next.dateTo = "";
          return next;
        });
      } catch {
        setCycle((c) => ({ ...c, start: FRONTEND_MIN_START, end: maxDate }));
      } finally {
        setLoadingCycle(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const resetForm = () => {
    setForm({
      dateFrom: "",
      dateTo: "",
      activityName: "",
      activityTypeId: "",
      activityCategoryId: "",
      activityOther: "",
      activityProvider: "",
      isParticipated: false,
      trainerActaAclp: "",
    });
    setProofOtherFiles([]);
    setProofPpFiles([]);
    if (proofOtherRef.current) proofOtherRef.current.value = "";
    if (proofPpRef.current) proofPpRef.current.value = "";
    setEditingId(null);
    setEditingStatus("");
    setEditRemarks("");
    setExistingDocs({ cc: [], vl: [], sst: [], pp: [] });
    setAiExtracting(false);
    setAiExtractDone(false);
    setAiExtractMsg("");
    setAiExtractEvidence([]);
  };

  const formatDate = (isoOrDMY) => {
    if (!isoOrDMY) return "-";
    if (typeof isoOrDMY === "string" && /^\d{2}-\d{2}-\d{4}$/.test(isoOrDMY)) {
      const [dd, mm, yyyy] = isoOrDMY.split("-");
      const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    }
    const d = new Date(isoOrDMY);
    return isNaN(d.getTime())
      ? "-"
      : d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const parseDMY = (s) => {
    if (!s || typeof s !== "string" || s.indexOf("-") < 0) return null;
    const [dd, mm, yyyy] = s.split("-");
    if (!dd || !mm || !yyyy) return null;
    return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  };

  const toInputDate = (val) => {
    if (!val) return "";
    if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
      const [dd, mm, yyyy] = val.split("-");
      return `${yyyy}-${mm}-${dd}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  // Selection helpers
  const findTypeById = (id) =>
    selects.activityTypes.find((x) => String(x.id) === String(id));
  const findCatById = (id) =>
    selects.learnCategories.find((x) => String(x.id) === String(id));

  const normalizeText = (v) =>
    String(v || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[\/()\-_,.]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const findSelectIdByName = (list, value) => {
    const target = normalizeText(value);
    if (!target) return "";

    const exact = list.find((x) => {
      const name = normalizeText(x.name);
      const desc = normalizeText(x.description);
      return name === target || desc === target;
    });
    if (exact) return String(exact.id);

    const contains = list.find((x) => {
      const name = normalizeText(x.name);
      const desc = normalizeText(x.description);
      return (
        (name && (name.includes(target) || target.includes(name))) ||
        (desc && (desc.includes(target) || target.includes(desc)))
      );
    });
    return contains ? String(contains.id) : "";
  };

  const dmyToInputDate = (val) => {
    if (!val) return "";
    const s = String(val).trim();

    const slash = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
    if (slash) {
      const dd = slash[1].padStart(2, "0");
      const mm = slash[2].padStart(2, "0");
      const yyyy = slash[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return s;

    const d = new Date(s);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  const applyAiExtractResult = (result) => {
    let extracted = result?.data || result || {};

    if (typeof extracted === "string") {
      try {
        extracted = JSON.parse(extracted);
      } catch {
        extracted = {};
      }
    }

    const dateFrom = dmyToInputDate(
      extracted.activityDateFrom ||
        extracted.dateFrom ||
        extracted.activity_date_from,
    );

    const dateTo = dmyToInputDate(
      extracted.activityDateTo ||
        extracted.dateTo ||
        extracted.activity_date_to ||
        extracted.activityDateFrom ||
        extracted.dateFrom,
    );

    const typeId = findSelectIdByName(
      selects.activityTypes,
      extracted.typeOfActivity || extracted.activityType || extracted.type,
    );

    const categoryId = findSelectIdByName(
      selects.learnCategories,
      extracted.learnCategory ||
        extracted.activityCategory ||
        extracted.category,
    );

    setForm((prev) => ({
      ...prev,
      dateFrom: dateFrom || prev.dateFrom,
      dateTo: dateTo || dateFrom || prev.dateTo,
      activityName:
        extracted.nameOfActivity || extracted.activityName || prev.activityName,
      activityTypeId: typeId || prev.activityTypeId,
      activityCategoryId: categoryId || prev.activityCategoryId,
      activityProvider:
        extracted.activityProvider ||
        extracted.provider ||
        prev.activityProvider,

      // default Yes when AI detects Classroom courses
      trainerActaAclp: String(extracted.typeOfActivity || "")
        .toLowerCase()
        .includes("classroom")
        ? "yes"
        : prev.trainerActaAclp,
    }));

    setAiExtractDone(true);
    setAiExtractEvidence(
      Array.isArray(extracted.evidence) ? extracted.evidence : [],
    );
    // setAiExtractMsg(
    //   "AI extracted the form fields. Please review before submitting.",
    // );
  };

  const selectedType = findTypeById(form.activityTypeId);

  // ✅ detect T2AG by name or description
  const isT2AG = useMemo(() => {
    const s = `${selectedType?.name || ""} ${selectedType?.description || ""}`
      .toLowerCase()
      .trim();
    return s.includes("t2ag");
  }, [selectedType]);

  const typeNameLC = useMemo(() => {
    return String(
      selectedType?.name || selectedType?.description || "",
    ).toLowerCase();
  }, [selectedType]);

  const isClassroomCourse = useMemo(() => {
    const s = (
      selectedType?.name ||
      selectedType?.description ||
      ""
    ).toLowerCase();
    return s.includes("classroom");
  }, [selectedType]);

  // ✅ default classroom trainer ACTA/ACLP = YES
  useEffect(() => {
    if (!isClassroomCourse) return;

    // only set default if user hasn't chosen yet
    setForm((f) => {
      if (f.trainerActaAclp) return f;
      return { ...f, trainerActaAclp: "yes" };
    });
  }, [isClassroomCourse]);

  // ✅ if T2AG -> no supporting doc needed
  const showOtherUpload =
    !form.activityTypeId ||
    (!isT2AG && (!isClassroomCourse || form.trainerActaAclp === "no"));

  // Draft can only be re-saved as Draft, or moved on to a full Submit.
  // A Pending / Re-submit item being edited can only be Submitted.
  const showDraftButton =
    !editingId || String(editingStatus).toLowerCase() === "draft";

  const otherAttachmentType = useMemo(() => {
    if (isClassroomCourse) return "ccNoOfAttach";

    if (typeNameLC.includes("site")) return "sstAttached";

    if (typeNameLC.includes("virtual")) return "vlAttached";

    return "vlAttached";
  }, [isClassroomCourse, typeNameLC]);

  useEffect(() => {
    if (!showOtherUpload && proofOtherFiles.length) {
      setProofOtherFiles([]);
      if (proofOtherRef.current) proofOtherRef.current.value = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOtherUpload, isT2AG]);

  // file validation
  const MB15 = 15 * 1024 * 1024;
  const allowed = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

  const addFiles = (prevList, fileList, maxCount = 3) => {
    const arr = Array.from(fileList || []);
    const valid = [];
    const skippedNames = [];
    const tooMany = [];

    const prevKey = (f) => `${f.name}:${f.size}`;
    const existing = new Set(prevList.map(prevKey));

    for (const f of arr) {
      if (f.size > MB15) {
        skippedNames.push(`${f.name} (too large)`);
        continue;
      }
      const k = `${f.name}:${f.size}`;
      if (existing.has(k)) {
        skippedNames.push(`${f.name} (duplicate)`);
        continue;
      }
      valid.push(f);
    }

    const spaceLeft = Math.max(0, maxCount - prevList.length);
    const toAdd = valid.slice(0, spaceLeft);
    const overflow = valid.slice(spaceLeft);
    overflow.forEach((f) =>
      tooMany.push(`${f.name} (exceeds limit of ${maxCount})`),
    );

    return {
      next: [...prevList, ...toAdd],
      skippedNames: [...skippedNames, ...tooMany],
    };
  };

  const handlePreviewLocalFile = (file) => {
    const previewWin = window.open("about:blank", "_blank");
    if (!previewWin) {
      alert("Popup blocked. Please allow popups for this site to preview files.");
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    previewWin.location.href = blobUrl;
  };

  const handleExtractLearnActivity = async () => {
    setFormError("");
    setAiExtractMsg("");
    setAiExtractEvidence([]);
    setAiExtractDone(false);

    if (proofPpFiles.length === 0) {
      setFormError("Please upload Proof of Participation first.");
      return;
    }

    try {
      setAiExtracting(true);
      // setAiExtractMsg("AI is reading your uploaded documents...");

      const fd = new FormData();

      // IMPORTANT: add text fields first
      fd.append("folderName", "learn");
      fd.append("module", "learn");

      proofPpFiles.forEach((file) => {
        fd.append("files", file);
      });

      proofOtherFiles.forEach((file) => {
        fd.append("files", file);
      });

      const { data } = await axios.post(ENDPOINT_AUTOFILL_AI_CHECKING, fd, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
        timeout: 180000,
      });

      if (!data?.success) {
        throw new Error(data?.message || "AI extraction failed.");
      }

      applyAiExtractResult(data.data);
    } catch (err) {
      setAiExtractDone(false);
      // setAiExtractMsg("");
      setFormError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to extract activity details.",
      );
    } finally {
      setAiExtracting(false);
    }
  };

  const validateForm = () => {
    if (!userId) return "Missing user id in localStorage.";
    if (!token) return "Missing auth token. Please login again.";
    if (proofPpFiles.length === 0 && existingDocs.pp.length === 0) {
      return "Please upload Proof of Participation before submitting.";
    }
    if (!form.dateFrom) return "Please select Activity Date From.";
    if (!form.dateTo) return "Please select Activity Date To.";
    const dFrom = new Date(form.dateFrom);
    const dTo = new Date(form.dateTo);
    if (dFrom > dTo) return "Invalid date range: From cannot be later than To.";
    if (!form.activityName.trim())
      return "Please provide the name of the activity.";
    if (!form.activityTypeId) return "Please select a type of activity.";
    if (!form.activityCategoryId) return "Please select a LEARN category.";
    const selectedCat = findCatById(form.activityCategoryId);
    if ((selectedCat?.name || "") === "Others" && !form.activityOther.trim())
      return "Please specify the other LEARN category.";
    if (isClassroomCourse && !form.trainerActaAclp)
      return "Please confirm whether the trainer has an ACTA/ACLP certification.";
    if (!form.activityProvider?.trim())
      return "Please provide the activity provider.";
    if (!form.isParticipated) return "You must confirm you participated.";

    const minStart = cycle.start || FRONTEND_MIN_START;
    const maxEnd = cycle.end;
    if (form.dateFrom < minStart || form.dateTo < minStart)
      return `Dates must be on/after ${minStart}.`;
    if (maxEnd && (form.dateFrom > maxEnd || form.dateTo > maxEnd))
      return `Dates must be on/before ${maxEnd}.`;
    return "";
  };

  // lighter validation for Save as Draft — skips the fields a draft is
  // allowed to leave incomplete (proof file, declaration, activity details)
  const validateDraftForm = () => {
    if (!userId) return "Missing user id in localStorage.";
    if (!token) return "Missing auth token. Please login again.";
    if (!form.dateFrom) return "Please select Activity Date From.";
    if (!form.dateTo) return "Please select Activity Date To.";
    const dFrom = new Date(form.dateFrom);
    const dTo = new Date(form.dateTo);
    if (dFrom > dTo) return "Invalid date range: From cannot be later than To.";

    const minStart = cycle.start || FRONTEND_MIN_START;
    const maxEnd = cycle.end;
    if (form.dateFrom < minStart || form.dateTo < minStart)
      return `Dates must be on/after ${minStart}.`;
    if (maxEnd && (form.dateFrom > maxEnd || form.dateTo > maxEnd))
      return `Dates must be on/before ${maxEnd}.`;
    return "";
  };

  // build delete list from existing docs
  const docIdOf = (d) => d?.doc_id ?? d?.id ?? d?.docId;

  const buildDeleteListForType = (attachmentType) => {
    if (!editingId) return [];
    const mapDocs = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((d) => ({ docId: docIdOf(d) }))
        .filter((x) => x.docId != null && x.docId !== "");
    switch (attachmentType) {
      case "ccNoOfAttach":
        return mapDocs(existingDocs.cc);
      case "vlAttached":
        return mapDocs(existingDocs.vl);
      case "sstAttached":
        return mapDocs(existingDocs.sst);
      case "ppAttached":
        return mapDocs(existingDocs.pp);
      default:
        return [];
    }
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const isAiDoneFromDocs = (docs) => {
    const arr = Array.isArray(docs) ? docs : [];
    if (!arr.length) return false;

    return arr.every((d) => {
      const hasStatus =
        d?.documentStatus != null && String(d.documentStatus).trim() !== "";
      const hasValid = typeof d?.isValid !== "undefined" && d?.isValid !== null;
      const hasReadable =
        typeof d?.isReadable !== "undefined" && d?.isReadable !== null;
      return hasStatus || hasValid || hasReadable;
    });
  };

  const pollAiUntilDone = async (learnId, maxTries = 25, waitMs = 2000) => {
    for (let i = 0; i < maxTries; i++) {
      try {
        const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
          params: { module: "learn", id: learnId },
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const docs = Array.isArray(data?.docs) ? data.docs : [];
        if (isAiDoneFromDocs(docs)) return true;
      } catch {
        // ignore and keep polling
      }
      await sleep(waitMs);
      setModalDetails(`Checking… (${i + 1}/${maxTries})`);
      setModalPct(100); // upload already done
    }
    return false;
  };

  async function uploadBucketsSequentially({
    learnId,
    otherFiles = [],
    ppFiles = [],
  }) {
    const jobs = [];
    if (otherFiles.length) {
      jobs.push({ type: otherAttachmentType, files: otherFiles });
    }
    if (ppFiles.length) jobs.push({ type: "ppAttached", files: ppFiles });
    if (!jobs.length) return;

    // AI object fields required by backend
    const aiName = displayName; // applicant name
    const aiDate = `${form.dateFrom} - ${form.dateTo}`; // backend expects req.body.date
    const aiTypeOfActivity =
      selectedType?.name || selectedType?.description || "";
    const aiProvider = form.activityProvider?.trim() || "";
    const aiActivityName = form.activityName?.trim() || "";

    const grandTotal = jobs.reduce(
      (sum, j) => sum + j.files.reduce((s, f) => s + (f.size || 0), 0),
      0,
    );
    let uploadedSoFar = 0;

    // show modal
    setShowUploadModal(true);
    setModalStage("uploading");
    setModalTitle("Uploading Documents");
    setModalMsg("Uploading your documents. Please do not close this page.");
    setModalPct(0);
    setModalDetails("");

    for (let idx = 0; idx < jobs.length; idx++) {
      const { type, files } = jobs[idx];

      const displayIndex = idx + 1;
      const jobsLen = jobs.length;
      const baseUploaded = uploadedSoFar;
      const jobTotal = files.reduce((s, f) => s + (f.size || 0), 0);

      const deleteList = buildDeleteListForType(type);
      const cleanDeleteList = (Array.isArray(deleteList) ? deleteList : [])
        .map((x) => ({ docId: x?.docId }))
        .filter(
          (x) =>
            x.docId !== undefined &&
            x.docId !== null &&
            String(x.docId).trim() !== "",
        );

      const fd = new FormData();

      // Required IDs
      fd.append("learnId", String(learnId));
      fd.append("doId", "");
      fd.append("attachmentType", type);
      fd.append("type", editingId ? "update" : "create");

      fd.append("name", aiName);
      fd.append("date", aiDate);
      fd.append("activityName", aiActivityName);
      fd.append("typeOfActivity", aiTypeOfActivity);
      fd.append("activityProvider", aiProvider);

      // deleteList
      if (cleanDeleteList.length > 0) {
        cleanDeleteList.forEach((item, i) => {
          fd.append(`deleteList[${i}][docId]`, String(item.docId));
        });
      } else {
        fd.append(`deleteList[0][docId]`, "-1");
      }

      for (const f of files) fd.append("files", f);

      const handleProgress = (evt) => {
        const loadedInThisJob = Math.min(evt.loaded || 0, jobTotal);
        const combinedLoaded = baseUploaded + loadedInThisJob;
        const pct = Math.min(
          100,
          Math.round((combinedLoaded * 100) / Math.max(grandTotal, 1)),
        );

        setModalPct(pct);
        setModalDetails(`Uploading ${displayIndex}/${jobsLen}`);
        setModalMsg("Uploading your documents. Please wait…");
      };

      await axios.post(ENDPOINT_UPLOAD, fd, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "x-userid": String(userId),
          "x-hid": String(learnId),
          "x-fldrname": "learn",
          "x-filenames": JSON.stringify([]),
        },
        timeout: 180000,
        onUploadProgress: handleProgress,
      });

      uploadedSoFar = baseUploaded + jobTotal;
      setModalPct(
        Math.min(
          100,
          Math.round((uploadedSoFar * 100) / Math.max(grandTotal, 1)),
        ),
      );
    }

    // Upload finished -> Checking AI
    setModalStage("checking");
    setModalTitle("Checking Documents");
    setModalMsg("Upload completed. Now checking your documents with AI.");
    setModalPct(100);
    setModalDetails("Starting…");

    // Poll until AI done (if AI enabled on backend, this will eventually become true)
    const ok = await pollAiUntilDone(learnId);

    setModalStage("done");
    setModalTitle("Completed");
    setModalMsg(
      ok
        ? "Upload and checking completed."
        : "Upload completed. Checking is still processing (please check later).",
    );
    setModalDetails("");
    setModalPct(100);

    setTimeout(() => {
      closeUploadModal();
    }, 1200);
  }

  const sanitizeYear = (val) => {
    const y = String(val || "")
      .replace(/[^\d]/g, "")
      .slice(0, 4);
    const yi = parseInt(y || "", 10);
    return Number.isFinite(yi) && yi >= 1900 ? yi : currentYear;
  };

  const fetchHistory = async ({ reset = false, page: pg = page } = {}) => {
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const yearParam = sanitizeYear(year);

      const { data } = await axios.get(ENDPOINT_HISTORY, {
        params: { module: "learn", userId, isAdmin: false, year: yearParam },
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 25000,
      });

      const rows = Array.isArray(data?.data) ? data.data : [];
      const qlc = q.trim().toLowerCase();

      const filtered = rows.filter((r) => {
        const name = (r.activity_name || "").toLowerCase();
        if (qlc && !name.includes(qlc)) return false;

        const fromVal = r.activity_date_from
          ? new Date(r.activity_date_from)
          : parseDMY(r.actDateFr);
        const toVal = r.activity_date_to
          ? new Date(r.activity_date_to)
          : parseDMY(r.actDateTo);

        const matchFrom = fromVal && fromVal.getFullYear() === yearParam;
        const matchTo = toVal && toVal.getFullYear() === yearParam;
        return matchFrom || matchTo || (!fromVal && !toVal);
      });

      const start = (pg - 1) * limit;
      const paged = filtered.slice(start, start + limit);
      const more = start + limit < filtered.length;

      setItems((prev) => (reset ? paged : [...prev, ...paged]));
      setHasMore(more);
      setHisError("");
    } catch (err) {
      setHisError(
        err?.response?.data?.message || "Unable to load history right now.",
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // load selects
  useEffect(() => {
    (async () => {
      try {
        setLoadingSelects(true);
        setSelectsError("");
        const { data } = await axios.get(ENDPOINT_SELECTS, {
          params: { module: "learn" },
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const rows = data?.data || data?.items || [];
        const isTypeOfActivity = (t) =>
          String(t || "").toLowerCase() === "typeofactivity";
        const isLearnCategory = (t) =>
          String(t || "").toLowerCase() === "learncategory";

        const activityTypes = rows
          .filter((r) => isTypeOfActivity(r.type))
          .map(({ id, name, description }) => ({ id, name, description }));

        const learnCategories = rows
          .filter((r) => isLearnCategory(r.type))
          .map(({ id, name, description }) => ({ id, name, description }));

        setSelects({ activityTypes, learnCategories });

        if (
          form.activityTypeId &&
          !activityTypes.some(
            (x) => String(x.id) === String(form.activityTypeId),
          )
        ) {
          setField("activityTypeId", "");
          setField("trainerActaAclp", "");
        }
        if (
          form.activityCategoryId &&
          !learnCategories.some(
            (x) => String(x.id) === String(form.activityCategoryId),
          )
        ) {
          setField("activityCategoryId", "");
          setField("activityOther", "");
        }
      } catch (err) {
        setSelectsError(
          err?.response?.data?.message || "Unable to load selection lists.",
        );
      } finally {
        setLoadingSelects(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load history
  useEffect(() => {
    fetchHistory({ reset: true, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const type = findTypeById(form.activityTypeId);
    const typeName = String(
      type?.name || type?.description || "",
    ).toLowerCase();

    if (typeName.includes("classroom")) {
      setForm((f) => ({
        ...f,
        trainerActaAclp: f.trainerActaAclp || "yes",
      }));
    } else {
      setForm((f) => ({
        ...f,
        trainerActaAclp: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.activityTypeId]);

  const onApplyFilters = async () => {
    setPage(1);
    await fetchHistory({ reset: true, page: 1 });
  };

  const onClearFilters = async () => {
    setQ("");
    setYear(String(currentYear));
    setPage(1);
    await fetchHistory({ reset: true, page: 1 });
  };

  const onLoadMore = async () => {
    const next = page + 1;
    setPage(next);
    await fetchHistory({ page: next, reset: false });
  };

  const saveActivity = async (statusName) => {
    const typeObj = findTypeById(form.activityTypeId);
    const catObj = findCatById(form.activityCategoryId);

    const activityType = typeObj?.id || "";
    const activityCategory = catObj?.id || "";
    const activityOther =
      (catObj?.name || "") === "Others" ? form.activityOther || "" : "";
    const classDeclare = form.trainerActaAclp === "yes" ? 1 : 0;

    const cntCc =
      !isT2AG && isClassroomCourse && form.trainerActaAclp === "no"
        ? proofOtherFiles.length + existingDocs.cc.length
        : 0;

    const cntVl =
      !isT2AG && !isClassroomCourse && otherAttachmentType === "vlAttached"
        ? proofOtherFiles.length + existingDocs.vl.length
        : 0;

    const cntSst =
      !isT2AG && !isClassroomCourse && otherAttachmentType === "sstAttached"
        ? proofOtherFiles.length + existingDocs.sst.length
        : 0;
    const cntPp = proofPpFiles.length + existingDocs.pp.length;
    const totalAttached = cntCc + cntVl + cntSst + cntPp;

    setSubmitting(true);
    try {
      const payload = {
        user_id: userId,
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        activityName: form.activityName,
        activityType,
        activityCategory,
        activityOther,
        classDeclare,
        isParticipated: Number(Boolean(form.isParticipated)),
        trainerActaAclp: form.trainerActaAclp || "",
        activityProvider: form.activityProvider?.trim() || "",
        ccNoOfAttach: cntCc,
        vlAttached: cntVl,
        sstAttached: cntSst,
        proofParticipation: cntPp,
        noOfAttached: totalAttached,

        statusName,
      };

      const isUpdate = Boolean(editingId);
      let endpoint = ENDPOINT_CREATE_LEARN;

      if (isUpdate) {
        endpoint = ENDPOINT_UPDATE_LEARN;
        payload.learnId = editingId;
      }

      const { data } = await axios.post(endpoint, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 25000,
      });

      if (!data?.success) throw new Error(data?.message || "Submit failed");

      const learnIdForUpload = isUpdate ? editingId : data?.data;

      // Upload + AI check (modal)
      try {
        await uploadBucketsSequentially({
          learnId: learnIdForUpload,
          otherFiles: showOtherUpload && !isT2AG ? proofOtherFiles : [],
          ppFiles: proofPpFiles,
        });
      } catch (upErr) {
        setFormError(
          upErr?.response?.data?.message ||
            upErr?.message ||
            "Activity saved, but attachments failed to upload. Please try again.",
        );
      }

      setWasDraftSave(statusName === "Draft");
      setTimeout(() => {
        setShowSuccess(true);
      }, 1300);
      resetForm();
      setPage(1);
      await fetchHistory({ reset: true, page: 1 });
    } catch (err2) {
      setFormError(
        err2?.response?.data?.message ||
          err2?.message ||
          "Failed to submit. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }

    await saveActivity("Pending");
  };

  const handleClickSaveDraft = () => {
    setFormError("");
    const err = validateDraftForm();
    if (err) {
      setFormError(err);
      return;
    }
    setShowDraftConfirm(true);
  };

  const handleConfirmSaveDraft = async () => {
    setShowDraftConfirm(false);
    await saveActivity("Draft");
  };

  const handleRequestDeleteDraft = (it) => {
    setDeleteError("");
    setDeleteTarget(it);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteDraft = async () => {
    const it = deleteTarget;
    if (!it) return;

    const actId =
      it.learn_id || it.learnId || it.id || it.activity_id || it.activityId;
    if (!actId) {
      setDeleteError("Unable to identify this draft.");
      return;
    }

    setDeleting(true);
    setDeleteError("");
    try {
      const { data } = await axios.delete(ENDPOINT_DELETE_ACTIVITY, {
        data: { module: "learn", act_id: actId },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (!data?.success)
        throw new Error(data?.message || "Failed to delete draft.");

      setShowDeleteConfirm(false);
      setDeleteTarget(null);

      if (
        editingId &&
        String(editingId) === String(actId) &&
        String(editingStatus).toLowerCase() === "draft"
      ) {
        resetForm();
      }

      setPage(1);
      await fetchHistory({ reset: true, page: 1 });
    } catch (e) {
      setDeleteError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to delete draft. Please try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleEditFromCard = async (it) => {
    const status = it?.status_name || "";
    const isEditable =
      String(status).toLowerCase() === "re-submit" ||
      String(status).toLowerCase() === "resubmit" ||
      String(status).toLowerCase() === "pending" ||
      String(status).toLowerCase() === "draft";
    if (!isEditable) return;

    const id =
      it.learn_id || it.learnId || it.id || it.activity_id || it.activityId;
    if (!id) return;

    try {
      setLoadingDetail(true);
      setFormError("");
      const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
        params: { module: "learn", id },
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 20000,
      });

      const rows = Array.isArray(data?.data) ? data.data : data?.data || [];
      const row = Array.isArray(rows) ? rows[0] : rows;
      const docs = Array.isArray(data?.docs) ? data.docs : [];

      if (!row) {
        setFormError("Unable to load activity details.");
        return;
      }

      let typeId = row.activity_type ? String(row.activity_type) : "";
      let categoryId = row.activity_category
        ? String(row.activity_category)
        : "";

      const othersText =
        row.activity_category_others ||
        row.activityCategoryOthers ||
        row.category_others ||
        "";
      const provider =
        row.activity_provider ||
        row.provider ||
        row.activityProvider ||
        row.orgName ||
        "";

      let trainerAns = "";
      if (row.trainerActaAclp === "yes" || row.trainerActaAclp === "no")
        trainerAns = row.trainerActaAclp;
      else if (row.class_declare === 1 || row.classDeclare === 1)
        trainerAns = "yes";
      else if (row.class_declare === 0 || row.classDeclare === 0)
        trainerAns = "no";

      const remarks =
        row.remarks || row.remark || row.comments || row.comment || "";

      const dateFrom =
        toInputDate(row.activity_date_from) || toInputDate(row.actDateFr);
      const dateTo =
        toInputDate(row.activity_date_to) || toInputDate(row.actDateTo);

      setForm({
        dateFrom: dateFrom || "",
        dateTo: dateTo || "",
        activityName: row.activity_name || row.name || "",
        activityTypeId: typeId || "",
        activityCategoryId: categoryId || "",
        activityOther: othersText || "",
        activityProvider: provider || "",
        isParticipated: true,
        trainerActaAclp: trainerAns || "",
      });

      const grouped = { cc: [], vl: [], sst: [], pp: [] };
      docs.forEach((d) => {
        switch (d.attachment_type) {
          case "ccNoOfAttach":
            grouped.cc.push(d);
            break;
          case "vlAttached":
            grouped.vl.push(d);
            break;
          case "sstAttached":
            grouped.sst.push(d);
            break;
          case "ppAttached":
            grouped.pp.push(d);
            break;
          default:
            break;
        }
      });
      setExistingDocs(grouped);

      setEditingId(row.learn_id || row.learnId || id);
      setEditingStatus(status);
      setEditRemarks(remarks || "");

      setProofOtherFiles([]);
      setProofPpFiles([]);
      if (proofOtherRef.current) proofOtherRef.current.value = "";
      if (proofPpRef.current) proofPpRef.current.value = "";

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setFormError(
        err?.response?.data?.message || "Unable to load activity details.",
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  const cancelEdit = () => resetForm();

  const formatDocDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div
      className="learn-wrap custom-font"
      style={{ background: PANEL_BG, minHeight: "100vh" }}
    >
      {/* Upload + Checking Modal */}
      <UploadCheckingModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        stage={modalStage}
        pct={modalPct}
        title={modalTitle}
        message={modalMsg}
        details={modalDetails}
      />

      {/* Top bar */}
      <header
        className="learn-topbar"
        style={{ height: 80, background: "#fff" }}
      >
        <div className="container-xxl h-100 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <img
              src={LOGO_URL}
              alt="TMIS"
              style={{ height: 50, objectFit: "contain" }}
            />
          </div>
          <div
            className="d-flex align-items-center gap-4"
            style={{ color: "#333" }}
          >
            <Bell size={20} color="#1A3584" />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
            />
            <span
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className="custom-font"
              onClick={() => navigate("/profile")}
            >
              {displayName}
            </span>
            <PersonCircle
              size={23}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/profile")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate("/profile");
              }}
            />
          </div>
        </div>
      </header>

      {/* Sub header */}
      <div
        style={{
          background: NAV_BLUE,
          color: "#fff",
          padding: "12px 0",
          boxShadow: "0 2px 0 rgba(0,0,0,0.04)",
        }}
      >
        <div
          className="container-xxl d-flex align-items-center"
          style={{ gap: 12 }}
        >
          <span
            role="button"
            aria-label="Go back"
            tabIndex={0}
            onClick={handleBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={18} color="#ffffff" />
          </span>
          <h4 className="m-0 fw-bold">Submission of LEARN activities</h4>
        </div>
      </div>

      <Container className="py-4">
        <Row className="g-4">
          {/* LEFT: Form */}
          <Col xs={12} lg={7} xl={7}>
            <Card
              className="shadow-sm"
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
                background: PANEL_SURFACE,
              }}
            >
              <Card.Body className="p-4 p-md-5">
                <p className="text-danger small">
                  * Only activities from 1st March 2026 onwards will be accepted
                </p>
                {editingId && (
                  <div className="mb-3">
                    <div
                      className="d-flex justify-content-between align-items-start"
                      style={{
                        background: "#EFF6FF",
                        border: "1px solid #BFDBFE",
                        borderRadius: 14,
                        padding: "14px 16px",
                        gap: 12,
                      }}
                    >
                      <div className="d-flex" style={{ gap: 10 }}>
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            minWidth: 34,
                            borderRadius: "50%",
                            background: "#DBEAFE",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PencilSquare size={16} color="#1D4ED8" />
                        </div>
                        <div>
                          <div
                            className="fw-bold"
                            style={{ color: "#1E3A8A", fontSize: 14.5 }}
                          >
                            Editing existing LEARN activity
                            {editingStatus && (
                              <span
                                className="ms-2"
                                style={{
                                  ...statusPillStyle(editingStatus),
                                  padding: "2px 8px",
                                  fontSize: 11,
                                }}
                              >
                                {editingStatus}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: "#1E40AF" }}>
                            You are updating a submission.
                          </div>
                          {loadingDetail && (
                            <div
                              className="mt-2 text-muted"
                              style={{ fontSize: 12 }}
                            >
                              <Spinner animation="border" size="sm" /> Loading
                              details…
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={cancelEdit}
                        style={{ borderRadius: 18, whiteSpace: "nowrap" }}
                      >
                        Cancel edit
                      </Button>
                    </div>

                    {editRemarks && (
                      <Alert variant="warning">
                        <div className="fw-bold mb-1">
                          Remarks from reviewer
                        </div>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {editRemarks}
                        </div>
                      </Alert>
                    )}
                  </div>
                )}

                {formError && (
                  <Alert variant="danger" className="mb-3">
                    {formError}
                  </Alert>
                )}
                {selectsError && (
                  <Alert variant="warning" className="mb-3">
                    {selectsError}
                  </Alert>
                )}

                <Form onSubmit={onSubmit}>
                  <StepHeader
                    number={1}
                    title="Upload proof and documents"
                    description="Upload Proof of Participation first like attendance email, course screenshot, lecture photo, or certificate. Click Check & Auto Fill to insert the activity fields automatically."
                  />

                  {/* Proof of Participation */}
                  <Form.Group className="mb-4" controlId="proofParticipation">
                    <div className="d-flex align-items-start justify-content-between">
                      <Form.Label className="mb-2 fw-bold">
                        Proof of Participation
                        <div
                          style={{ fontSize: "12px", color: "#1F2937" }}
                          className="fw-normal"
                        >
                          (Maximum 3 files allowed, ≤ 15 MB)
                        </div>
                        {proofPpFiles.length > 0 && (
                          <div
                            style={{ fontSize: "12px" }}
                            className="fw-normal"
                          >
                            {proofPpFiles.length} / 3 selected
                          </div>
                        )}
                      </Form.Label>

                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => proofPpRef.current?.click()}
                        style={{ borderRadius: 18, lineHeight: 1.1 }}
                        type="button"
                      >
                        Select Files
                      </Button>
                    </div>

                    {existingDocs.pp.length > 0 && (
                      <div
                        className="mt-2"
                        style={{
                          fontSize: 12,
                          background: "#EFF6FF",
                          borderRadius: 8,
                          padding: "8px 10px",
                        }}
                      >
                        <div className="fw-bold mb-1">
                          Existing proof of participation
                        </div>
                        <ul className="mb-0" style={{ paddingLeft: 18 }}>
                          {existingDocs.pp.map((d) => (
                            <li key={d.doc_id ?? d.id}>
                              {d.file_name}{" "}
                              <span className="text-muted">
                                ({formatDocDate(d.uploaded_date)})
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-1 text-muted">
                          You can add more files if needed.
                        </div>
                      </div>
                    )}

                    <input
                      ref={proofPpRef}
                      type="file"
                      name="files"
                      accept={allowed}
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const { next, skippedNames } = addFiles(
                          proofPpFiles,
                          e.target.files,
                          3,
                        );
                        setProofPpFiles(next);
                        if (skippedNames.length)
                          setFormError(`Skipped: ${skippedNames.join(", ")}`);
                        e.target.value = "";
                      }}
                    />

                    <div
                      className="mt-1"
                      style={{
                        background: "#fff",
                        border: "2px dashed #cfd6e0",
                        color: "#1F2937",
                        borderRadius: 12,
                        padding: "16px 14px",
                        minHeight: 56,
                      }}
                    >
                      {proofPpFiles.length === 0 ? (
                        <div>No file selected</div>
                      ) : (
                        <ul className="mb-0" style={{ paddingLeft: 16 }}>
                          {proofPpFiles.map((f, idx) => (
                            <li
                              key={`${f.name}-${f.size}-${idx}`}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span
                                className="me-2"
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {f.name} ({Math.round(f.size / 1024)} KB)
                              </span>
                              <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handlePreviewLocalFile(f)}
                                style={{ borderRadius: 18 }}
                                type="button"
                              >
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  const updated = proofPpFiles.filter(
                                    (_, i) => i !== idx,
                                  );

                                  setProofPpFiles(updated);

                                  // if no proof file left → clear form
                                  if (updated.length === 0) {
                                    setForm({
                                      dateFrom: "",
                                      dateTo: "",
                                      activityName: "",
                                      activityTypeId: "",
                                      activityCategoryId: "",
                                      activityOther: "",
                                      activityProvider: "",
                                      isParticipated: false,
                                      trainerActaAclp: "",
                                    });

                                    setProofOtherFiles([]);

                                    if (proofOtherRef.current)
                                      proofOtherRef.current.value = "";
                                    if (proofPpRef.current)
                                      proofPpRef.current.value = "";

                                    // clear AI states
                                    setAiExtractDone(false);
                                    setAiExtractMsg("");
                                    setAiExtractEvidence([]);
                                  }
                                }}
                                style={{ borderRadius: 18 }}
                                type="button"
                              >
                                Remove
                              </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Form.Group>

                  <div className="mb-4">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Button
                        variant={aiExtractDone ? "success" : "primary"}
                        type="button"
                        onClick={handleExtractLearnActivity}
                        disabled={
                          aiExtracting ||
                          loadingSelects ||
                          proofPpFiles.length === 0
                        }
                        style={{ borderRadius: 20, minWidth: 180 }}
                      >
                        {aiExtracting ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            Checking your files...
                          </>
                        ) : aiExtractDone ? (
                          "Fields Inserted"
                        ) : (
                          "Check & Auto Fill"
                        )}
                      </Button>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Upload proof first, then click Check.
                      </span>
                    </div>

                    {aiExtractMsg && (
                      <Alert
                        variant={aiExtractDone ? "success" : "info"}
                        className="mt-3 mb-0"
                      >
                        <div>{aiExtractMsg}</div>
                        {aiExtractEvidence.length > 0 && (
                          <ul className="mb-0 mt-2" style={{ paddingLeft: 18 }}>
                            {aiExtractEvidence.slice(0, 4).map((x, i) => (
                              <li key={i}>{x}</li>
                            ))}
                          </ul>
                        )}
                      </Alert>
                    )}
                  </div>
                  <StepHeader
                    number={2}
                    title="Read & Confirm Information"
                    description="Please carefully review the auto-filled activity information and make sure all details are correct before submitting. If any information is incorrect or missing, you can manually update the fields before continuing."
                  />
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="learnFrom">
                        <Form.Label className="mb-1 fw-bold">
                          Activity Date From
                          <Required />
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateFrom}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField("dateFrom", v);
                            if (form.dateTo && v && form.dateTo < v)
                              setField("dateTo", "");
                          }}
                          min={cycle.start || FRONTEND_MIN_START}
                          max={new Date().toISOString().slice(0, 10)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="learnTo">
                        <Form.Label className="mb-1 fw-bold">
                          Activity Date To
                          <Required />
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateTo}
                          onChange={(e) => setField("dateTo", e.target.value)}
                          min={
                            form.dateFrom || cycle.start || FRONTEND_MIN_START
                          }
                          max={cycle.end || undefined}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="learnName">
                    <Form.Label className="mb-1 fw-bold">
                      Name of Activity
                      <Required />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={form.activityName}
                      onChange={(e) => setField("activityName", e.target.value)}
                      style={{ background: "#fff" }}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="learnType">
                    <Form.Label className="mb-1 fw-bold">
                      Type of Activity
                      <Required />
                    </Form.Label>
                    <Form.Select
                      value={form.activityTypeId}
                      onChange={(e) =>
                        setField("activityTypeId", e.target.value)
                      }
                      required
                      disabled={loadingSelects || !!selectsError}
                    >
                      <option value="">
                        {loadingSelects ? "Loading..." : "-- Select Type --"}
                      </option>
                      {selects.activityTypes.map((t) => (
                        <option
                          key={t.id}
                          value={t.id}
                          title={t.description || ""}
                        >
                          {t.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {isClassroomCourse && !isT2AG && (
                    <Form.Group className="mb-3" controlId="trainerActaAclp">
                      <Form.Label className="mb-1 fw-bold">
                        Does the trainer have an ACTA/ACLP certification?
                        <Required />
                      </Form.Label>
                      <div className="d-flex gap-2">
                        {["yes", "no"].map((val) => {
                          const selected = form.trainerActaAclp === val;
                          return (
                            <Button
                              key={val}
                              type="button"
                              onClick={() => setField("trainerActaAclp", val)}
                              style={{
                                borderRadius: 20,
                                minWidth: 96,
                                fontWeight: 600,
                                background: selected ? "#2563EB" : "#fff",
                                color: selected ? "#fff" : "#334155",
                                border: selected
                                  ? "1px solid #2563EB"
                                  : "1px solid #cbd5e1",
                              }}
                            >
                              {val === "yes" ? "Yes" : "No"}
                            </Button>
                          );
                        })}
                      </div>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3" controlId="learnCategory">
                    <Form.Label className="mb-1 fw-bold">
                      LEARN Category
                      <Required />
                    </Form.Label>
                    <Form.Select
                      value={form.activityCategoryId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setField("activityCategoryId", id);
                        const found = selects.learnCategories.find(
                          (x) => String(x.id) === String(id),
                        );
                        if ((found?.name || "") !== "Others")
                          setField("activityOther", "");
                      }}
                      required
                      disabled={loadingSelects || !!selectsError}
                    >
                      <option value="">
                        {loadingSelects
                          ? "Loading..."
                          : "-- Select Category --"}
                      </option>
                      {selects.learnCategories.map((c) => (
                        <option
                          key={c.id}
                          value={c.id}
                          title={c.description || ""}
                        >
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {(() => {
                    const found = findCatById(form.activityCategoryId);
                    if ((found?.name || "") !== "Others") return null;
                    return (
                      <Form.Group className="mb-3" controlId="learnOther">
                        <Form.Label className="mb-1 fw-bold">
                          Please specify
                          <Required />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Describe the LEARN category"
                          value={form.activityOther}
                          onChange={(e) =>
                            setField("activityOther", e.target.value)
                          }
                          required
                        />
                      </Form.Group>
                    );
                  })()}

                  <Form.Group className="mb-3" controlId="activityProvider">
                    <Form.Label className="mb-1 fw-bold">
                      Activity Provider
                      <Required />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Please enter the name of the organisation"
                      value={form.activityProvider}
                      onChange={(e) =>
                        setField("activityProvider", e.target.value)
                      }
                    />
                  </Form.Group>

                  {/* Upload (Other) */}
                  {showOtherUpload && (
                    <Form.Group className="mb-4" controlId="proofOtherActivity">
                      <div className="d-flex align-items-start justify-content-between">
                        <Form.Label className="mb-2 fw-bold">
                          Supporting Document (Other Activities / Classroom
                          Declaration)
                          <div
                            style={{ fontSize: "12px", color: "#1F2937" }}
                            className="fw-normal"
                          >
                            (Maximum 3 files allowed, ≤ 15 MB)
                          </div>
                          {proofOtherFiles.length > 0 && (
                            <div
                              style={{ fontSize: "12px" }}
                              className="fw-normal"
                            >
                              {proofOtherFiles.length} / 3 selected
                            </div>
                          )}
                        </Form.Label>

                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => proofOtherRef.current?.click()}
                          style={{ borderRadius: 18, lineHeight: 1.1 }}
                          type="button"
                        >
                          Select Files
                        </Button>
                      </div>

                      {(existingDocs.cc.length > 0 ||
                        existingDocs.vl.length > 0 ||
                        existingDocs.sst.length > 0) && (
                        <div
                          className="mt-2"
                          style={{
                            fontSize: 12,
                            background: "#EFF6FF",
                            borderRadius: 8,
                            padding: "8px 10px",
                          }}
                        >
                          <div className="fw-bold mb-1">
                            Existing supporting documents
                          </div>
                          <ul className="mb-0" style={{ paddingLeft: 18 }}>
                            {existingDocs.cc.map((d) => (
                              <li key={d.doc_id ?? d.id}>
                                Classroom Courses – {d.file_name}{" "}
                                <span className="text-muted">
                                  ({formatDocDate(d.uploaded_date)})
                                </span>
                              </li>
                            ))}
                            {existingDocs.vl.map((d) => (
                              <li key={d.doc_id ?? d.id}>
                                Virtual – {d.file_name}{" "}
                                <span className="text-muted">
                                  ({formatDocDate(d.uploaded_date)})
                                </span>
                              </li>
                            ))}
                            {existingDocs.sst.map((d) => (
                              <li key={d.doc_id ?? d.id}>
                                Site-spec – {d.file_name}{" "}
                                <span className="text-muted">
                                  ({formatDocDate(d.uploaded_date)})
                                </span>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-1 text-muted">
                            You can add more files if needed.
                          </div>
                        </div>
                      )}

                      <input
                        ref={proofOtherRef}
                        type="file"
                        name="files"
                        accept={allowed}
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const { next, skippedNames } = addFiles(
                            proofOtherFiles,
                            e.target.files,
                            3,
                          );
                          setProofOtherFiles(next);
                          if (skippedNames.length)
                            setFormError(`Skipped: ${skippedNames.join(", ")}`);
                          e.target.value = "";
                        }}
                      />

                      <div
                        className="mt-1"
                        style={{
                          background: "#fff",
                          border: "2px dashed #cfd6e0",
                          color: "#1F2937",
                          borderRadius: 12,
                          padding: "16px 14px",
                          minHeight: 56,
                        }}
                      >
                        {proofOtherFiles.length === 0 ? (
                          <div>No file selected</div>
                        ) : (
                          <ul className="mb-0" style={{ paddingLeft: 16 }}>
                            {proofOtherFiles.map((f, idx) => (
                              <li
                                key={`${f.name}-${f.size}-${idx}`}
                                className="d-flex justify-content-between align-items-center"
                              >
                                <span
                                  className="me-2"
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {f.name} ({Math.round(f.size / 1024)} KB)
                                </span>
                                <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handlePreviewLocalFile(f)}
                                  style={{ borderRadius: 18 }}
                                  type="button"
                                >
                                  View
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() =>
                                    setProofOtherFiles((list) =>
                                      list.filter((_, i) => i !== idx),
                                    )
                                  }
                                  style={{ borderRadius: 18 }}
                                  type="button"
                                >
                                  Remove
                                </Button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-4" controlId="isParticipated">
                    <Form.Check
                      type="checkbox"
                      label="I declare that the information and documents submitted for this LEARN activity are true, accurate and complete to the best of my knowledge."
                      checked={form.isParticipated}
                      onChange={(e) =>
                        setField("isParticipated", e.target.checked)
                      }
                      className="small"
                    />
                  </Form.Group>

                  <div className="text-center d-flex justify-content-center gap-2">
                    {showDraftButton && (
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={handleClickSaveDraft}
                        disabled={submitting || loadingSelects}
                        style={{
                          minWidth: 140,
                          borderRadius: 20,
                        }}
                      >
                        Save as Draft
                      </Button>
                    )}
                    <Button
                      type="submit"
                      disabled={
                        submitting ||
                        !form.isParticipated ||
                        loadingSelects ||
                        (proofPpFiles.length === 0 &&
                          existingDocs.pp.length === 0)
                      }
                      style={{
                        background: BRAND,
                        border: "none",
                        minWidth: 140,
                        borderRadius: 20,
                      }}
                    >
                      {submitting
                        ? editingId
                          ? "Updating..."
                          : "Submitting..."
                        : editingId
                          ? "Ready to Submit"
                          : "Submit"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* RIGHT: History */}
          <Col xs={12} lg={5} xl={5}>
            <Card
              className="shadow-sm"
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
                background: PANEL_SURFACE,
              }}
            >
              <Card.Body className="p-3 p-md-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="m-0 fw-bold">History of LEARN activities</h5>
                </div>

                <div className="mb-3">
                  <InputGroup className="mb-2">
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search by activity name"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </InputGroup>

                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      type="number"
                      min="1900"
                      max={currentYear}
                      placeholder="Year (e.g., 2025)"
                      value={year}
                      onChange={(e) =>
                        setYear(
                          e.target.value.replace(/[^\d]/g, "").slice(0, 4),
                        )
                      }
                      title="Filter by year"
                      style={{ maxWidth: 180 }}
                    />
                    <Button variant="primary" onClick={onApplyFilters}>
                      Apply
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={onClearFilters}
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div
                  style={{
                    maxHeight: 520,
                    overflow: "auto",
                    border: "1px solid #eef0f3",
                    borderRadius: 12,
                    padding: 8,
                  }}
                >
                  {loading ? (
                    <div className="py-4 text-center text-muted">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading…
                    </div>
                  ) : hisError ? (
                    <div className="py-4 text-center text-danger">
                      {hisError}
                    </div>
                  ) : items.length === 0 ? (
                    <div className="py-4 text-center text-muted">
                      No activities found.
                    </div>
                  ) : (
                    items.map((it, i) => {
                      const fromText = formatDate(
                        it.activity_date_from || it.actDateFr,
                      );
                      const toText = formatDate(
                        it.activity_date_to || it.actDateTo,
                      );
                      const typeText =
                        it.typeOfActivity ||
                        it.activity_type ||
                        it.activityType ||
                        "—";
                      const catText =
                        it.category ||
                        (it.activity_category === "Others" &&
                        it.activity_category_others
                          ? `Others (${it.activity_category_others})`
                          : it.activity_category || it.learnCategory || "—");

                      return (
                        <ActivityCard
                          key={
                            it.id ||
                            `${it.activity_date_from}-${it.activity_name}-${i}`
                          }
                          it={it}
                          fromText={fromText}
                          toText={toText}
                          typeText={typeText}
                          catText={catText}
                          onEdit={handleEditFromCard}
                          onViewProfile={() => navigate("/profile")}
                          onDelete={handleRequestDeleteDraft}
                        />
                      );
                    })
                  )}
                </div>

                {!loading && items.length > 0 && hasMore && (
                  <div className="text-center mt-3">
                    <Button
                      variant="outline-primary"
                      onClick={onLoadMore}
                      disabled={loadingMore}
                      style={{ borderRadius: 999, minWidth: 140 }}
                    >
                      {loadingMore ? "Loading…" : "Load more"}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Success Modal */}
      <Modal
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        centered
        backdrop="static"
        keyboard
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: wasDraftSave
                ? "rgba(139,92,246,0.12)"
                : "rgba(16,185,129,0.12)",
            }}
          >
            {wasDraftSave ? (
              <PencilSquare size={40} color="#6D28D9" />
            ) : (
              <CheckCircleFill size={44} color="#10B981" />
            )}
          </div>
          <h5 className="fw-bold mb-2">
            {wasDraftSave
              ? "Draft Saved"
              : editingId
                ? "Update Successful"
                : "Submission Successful"}
          </h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            {wasDraftSave
              ? "Your activity was saved as a draft. You can continue editing it anytime from your LEARN history below."
              : "Your activity was submitted successfully."}
          </div>
          <div className="d-flex justify-content-center gap-2">
            <Button
              onClick={() => {
                setShowSuccess(false);
                setWasDraftSave(false);
                setEditingId(null);
                setEditingStatus("");
                setEditRemarks("");
                setExistingDocs({ cc: [], vl: [], sst: [], pp: [] });
              }}
              style={{ background: BRAND, border: "none", minWidth: 120 }}
            >
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Save as Draft confirmation */}
      <Modal
        show={showDraftConfirm}
        onHide={() => setShowDraftConfirm(false)}
        centered
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(139,92,246,0.12)",
            }}
          >
            <PencilSquare size={32} color="#6D28D9" />
          </div>
          <h5 className="fw-bold mb-2">Save as Draft?</h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            This activity will be saved as a draft. It won't be reviewed
            until you come back and submit it.
          </div>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => setShowDraftConfirm(false)}
              disabled={submitting}
              style={{ minWidth: 100, borderRadius: 20 }}
            >
              No
            </Button>
            <Button
              onClick={handleConfirmSaveDraft}
              disabled={submitting}
              style={{
                background: "#6D28D9",
                border: "none",
                minWidth: 100,
                borderRadius: 20,
              }}
            >
              {submitting ? "Saving..." : "Yes"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Draft confirmation */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => {
          if (!deleting) {
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
            setDeleteError("");
          }
        }}
        centered
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(239,68,68,0.12)",
            }}
          >
            <TrashFill size={28} color="#B91C1C" />
          </div>
          <h5 className="fw-bold mb-2">Delete this draft?</h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            This will permanently delete this draft LEARN activity and any
            files you've uploaded for it. This cannot be undone.
          </div>
          {deleteError && (
            <Alert variant="danger" className="text-start mb-3">
              {deleteError}
            </Alert>
          )}
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteTarget(null);
                setDeleteError("");
              }}
              disabled={deleting}
              style={{ minWidth: 100, borderRadius: 20 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteDraft}
              disabled={deleting}
              style={{
                background: "#DC2626",
                border: "none",
                minWidth: 100,
                borderRadius: 20,
              }}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
